import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CandidateByStageType } from './JobDetailsSidebar';
import { theme } from '../../../../theme';
import { StatusChip } from '../../CandidateManagement/CandidateContent/CandidateContent.styles';
import { statusColorMap } from '../CreateJobPosting/CreateJob/ReadJob';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

interface CandidatePopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  candidates: CandidateByStageType[];
  open: boolean;
  loading: boolean;
  navigateToHiringPipeline: (value: number) => void;
  showStatus: boolean
}

const CandidatePopover: React.FC<CandidatePopoverProps> = ({
  anchorEl,
  onClose,
  candidates,
  open,
  loading,
  navigateToHiringPipeline,
  showStatus
}) => {
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    return candidates.filter((candidate) =>
      candidate.CandidateName.toLowerCase().includes(search.toLowerCase())
    );
  }, [search, candidates]);

  useEffect(() => {
    if (open) {
      setSearch('');
    }
  }, [open]);


  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: {
          mt: 1,
          ml: 1,
          width: '21vw',
          maxHeight: '32vw',
          p: 1.5,
        },
      }}
    >
      <Box>
        <TextField
          fullWidth
          size="small"
          placeholder="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: '0.8vw' }}
        />

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={150} mt={2}>
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 1 }}>
              Loading...
            </Typography>
          </Box>
        ) : filtered.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={150}>
            <Typography variant="body2" color="text.secondary">
              No Candidates Found
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{ maxHeight: 300, overflowY: 'auto', mt: 0.5 }}>
            {filtered.map((candidate, index) => {
              const statusColor = statusColorMap[candidate.CandidateStatus]?.color || '#1565C0';
              const bgColor = statusColorMap[candidate.CandidateStatus]?.bgColor || '#E3F2FD'
              const borderColor =statusColorMap[candidate.CandidateStatus]?.borderColor || '#BBDEFB'
              // const color= statusColorMap[candidate.CandidateStatus]?.
              return (
                <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                  <ListItemAvatar sx={{ minWidth: 'auto', mr: 1, position: 'relative' }}>
                    <Avatar sx={{ width: 32, height: 32 }}>
                      {candidate.CandidateName?.[0] || '?'}
                    </Avatar>
                    {candidate.CandidateStatus && (
                      <Tooltip
                        title={candidate.CandidateStatus}
                        componentsProps={{
                          tooltip: {
                            sx: {
                              backgroundColor: bgColor,
                              color: statusColor,
                              fontSize: 12,
                              boxShadow: 1,
                              border: `1px solid ${borderColor}`,
                              ml:10
                            },
                          },
                          popper: {
                            sx: {
                              [`& .MuiTooltip-arrow`]: {
                                color: '#fff', // Tooltip arrow background (matches tooltip background)
                              },
                            },
                          },
                        }}
                      >
                        <FiberManualRecordIcon
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            right: 0,
                            fontSize: 10,
                            color: statusColor,
                            backgroundColor: '#fff',
                            borderRadius: '50%',
                          }}
                        />
                      </Tooltip>


                    )}
                  </ListItemAvatar>
                  <ListItemText
                    onClick={() => navigateToHiringPipeline(candidate.MapID)}
                    primary={
                      <Typography
                        sx={{
                          wordBreak: 'break-word',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          cursor: 'pointer',
                        }}
                        variant="body2"
                        title={candidate.CandidateName}
                      >
                        {candidate.CandidateName}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Popover>

  );
};

export default CandidatePopover;
